import React, { useEffect, useState } from 'react'
import { useStores } from 'hooks/useStores'
import { observer } from 'mobx-react'
import { toast } from 'react-toastify'
import dayjs from 'dayjs'
import WorkEmailVerificationModal from './WorkEmailVerificationModal'
import EmailVerificationModal from './EmailVerificationModal'
import { EmailVerificationResponse } from 'interfaces/common'

enum WorkEmailModalType {
  WORK_EMAIL_INPUT = 'work_email_input',
  OTP_VERIFICATION = 'otp_verification',
  NONE = 'none'
}

interface WorkEmailVerificationFlowProps {
  children?: React.ReactNode
}

const WorkEmailVerificationFlow: React.FC<WorkEmailVerificationFlowProps> = ({ children }) => {
  const { authStore, userStore, organizationStore, ssoConfigStore } = useStores()
  const { userDetail } = authStore
  const { organization } = organizationStore
  const { ssoConfigs } = ssoConfigStore

  const [showWorkEmailModal, setShowWorkEmailModal] = useState(false)
  const [showOtpModal, setShowOtpModal] = useState(false)

  const { isEmailVerifying } = userStore

  // Get the enabled SSO config's emailBindCutoffDate
  const getEmailBindCutoffDate = (): string | undefined => {
    const enabledConfig = ssoConfigs.find(config => config.enabled)
    return enabledConfig?.config?.emailBindCutoffDate
  }

  const getDeadlineDate = (): string => {
    const cutoffDate = getEmailBindCutoffDate()
    if (!cutoffDate) {
      const today = new Date()
      const deadline = new Date(today)
      deadline.setDate(today.getDate() + 30)
      return dayjs(deadline).format('DD-MMM-YYYY').toUpperCase()
    }
    return dayjs(cutoffDate).format('DD-MMM-YYYY').toUpperCase()
  }

  const isPastDeadline = (): boolean => {
    const cutoffDate = getEmailBindCutoffDate()
    if (!cutoffDate) {
      return false
    }
    const today = new Date()
    const deadline = new Date(cutoffDate)
    deadline.setHours(23, 59, 59, 999)
    return today > deadline
  }

  const getRequiredModalType = (): WorkEmailModalType => {
    if (!organization?.isSsoEnable) {
      return WorkEmailModalType.NONE
    }

    if (ssoConfigs?.length === 0) {
      return WorkEmailModalType.NONE
    }

    if (userDetail?.needBindEmail === true) {
      return WorkEmailModalType.WORK_EMAIL_INPUT
    }

    return WorkEmailModalType.NONE
  }

  useEffect(() => {
    const modalType = getRequiredModalType()

    if (modalType === WorkEmailModalType.WORK_EMAIL_INPUT) {
      setShowWorkEmailModal(true)
      setShowOtpModal(false)
    } else if (modalType === WorkEmailModalType.OTP_VERIFICATION) {
      setShowOtpModal(true)
      setShowWorkEmailModal(false)
    } else {
      setShowWorkEmailModal(false)
      setShowOtpModal(false)
    }
  }, [userDetail?.needBindEmail, organization?.isSsoEnable, ssoConfigs.length])

  const handleWorkEmailSubmit = async (workEmail: string) => {
    const response: EmailVerificationResponse = await userStore.bindWorkEmail(workEmail)
    if (response.success) {
      setShowWorkEmailModal(false)
      setShowOtpModal(true)
      toast.success(response.message)
    } else {
      toast.error(response.message)
    }
  }

  const handleOtpVerify = async (otp: string) => {
    const response: EmailVerificationResponse = await userStore.verifyWorkEmail(otp)
    if (response.success) {
      setShowOtpModal(false)
      toast.success(response.message)
    } else {
      toast.error(response.message)
    }
  }

  const handleOtpResend = async () => {
    const response: EmailVerificationResponse = await userStore.resendWorkEmailVerification()
    if (response.success) {
      toast.success(response.message)
    } else {
      toast.error(response.message)
    }
  }

  const handleSkipOtp = () => {
    if (!isEmailVerifying && !isPastDeadline()) {
      setShowOtpModal(false)
    }
  }

  const handleSkipWorkEmail = () => {
    if (!isEmailVerifying) {
      setShowWorkEmailModal(false)
    }
  }

  useEffect(() => {
    authStore.getMyUser()
    if (organization?.id) {
      ssoConfigStore.fetchSSOConfigs(organization.id)
    }
  }, [organization?.id])

  return (
    <>
      {/* Work Email Input Modal */}
      <WorkEmailVerificationModal
        isOpen={showWorkEmailModal}
        onClose={() => {}}
        onSkip={handleSkipWorkEmail}
        onSubmit={handleWorkEmailSubmit}
        isLoading={isEmailVerifying}
        deadlineDate={getDeadlineDate()}
        isPastDeadline={isPastDeadline()}
        initialEmail={userDetail?.ssoEmail || ''}
      />

      {/* OTP Verification Modal */}
      <EmailVerificationModal
        isOpen={showOtpModal}
        onClose={() => {}}
        onSkip={handleSkipOtp}
        onVerify={handleOtpVerify}
        onResend={handleOtpResend}
        isLoading={isEmailVerifying}
        deadlineDate={getDeadlineDate()}
        isPastDeadline={isPastDeadline()}
      />
    </>
  )
}

export default observer(WorkEmailVerificationFlow)
